import {
  EApplicantType,
  EContractStatus,
  ECreditApplicationStatus,
  ECreditApplicationStep,
  EDecision,
  EDocumentStatus,
  EFinancingProgram,
  EOriginSystemId,
  EWorksheetStatus,
} from '@if/core/types'
import {
  anyDocsAwaitingApproval,
  anyDocsAwaitingUploadOrRefused,
  availableCreditEqualApprovedAmount,
  checkCanCreateContract,
  cvtAwaitingApproval,
  isFundingStarted,
} from '@src/data/creditapp-selectors'
import {
  getIdentityDocumentTypes,
  getIncomeVerificationDocumentsTypes,
  hasRequiredDocuments,
} from '@src/data/income-selectors'
import { ECurrentCustomerStep, FinancingProgramConfigs } from '@src/types/Constants'
import { ApplicantDto, CreditAlertsDto, CreditApplication } from '@src/types/CreditApplicationSchema'
import { FilteredWorksheet } from '@src/types/FilteredWorksheet'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { canPollCreditApp, isAnalysisActive, mustAskApplicantSIN, mustAskCoapplicantSIN } from './credit-hooks'

function requiresMerchantIntervention(creditAlerts: CreditAlertsDto) {
  return (
    creditAlerts.hasDoBMismatch ||
    creditAlerts.hasNameMismatch ||
    creditAlerts.hasCreditLocked ||
    creditAlerts.hasInvalidCity ||
    creditAlerts.hasGeneralCreditReportError ||
    creditAlerts.isBanned
  )
}

export enum ECreditApplicationStatusMessage {
  Draft = 'draft',
  Active = 'active',
  Completed = 'completed',
  Cancelled = 'cancelled',
  Expired = 'expired',
  Declined = 'declined',
  ManualRevision = 'manualRevision',
  DocumentsRequired = 'documentsRequired',
  Error = 'applicationError',
  Progress = 'inProgress',
  Waiting = 'waiting',
  Processing = 'processing',
  ActionRequired = 'actionRequired',
  AwaitingSignature = 'awaitingSignature',
  AwaitingApproval = 'awaitingApproval',
  CreditReportNotFound = 'creditReportNotFound',
  SinRequired = 'sinRequired',
}

export const GetStatusFromDecision = (
  decision: EDecision | undefined,
  isprequal: boolean,
  creditApp: CreditApplication,
  isAnalysing: boolean,
  docsAwaitingApproval: boolean,
): ECreditApplicationStatusMessage | null => {
  const docsAwaitingUploadOrRefused = anyDocsAwaitingUploadOrRefused(
    creditApp.requiredDocuments,
    ECreditApplicationStep.Credit,
  )

  const requiresInterventionConfig =
    FinancingProgramConfigs[creditApp.financingProgramId as EFinancingProgram].requiresCreditStepIntervention

  const applicantRequiresIntervention =
    creditApp.applicant.creditAlerts != null ? requiresMerchantIntervention(creditApp.applicant.creditAlerts) : false
  const coApplicantRequiresIntervention =
    creditApp.coApplicant && creditApp.coApplicant.creditAlerts != null
      ? requiresMerchantIntervention(creditApp.coApplicant.creditAlerts)
      : false

  const requiresMerchantInterventionFlag =
    requiresInterventionConfig && (applicantRequiresIntervention || coApplicantRequiresIntervention)

  if (docsAwaitingUploadOrRefused && decision === EDecision.ManualRevision) {
    return ECreditApplicationStatusMessage.DocumentsRequired
  }

  if (docsAwaitingApproval && decision !== EDecision.Approved && decision !== EDecision.Refused) {
    return ECreditApplicationStatusMessage.AwaitingApproval
  }

  if (creditApp.status === ECreditApplicationStatus.Cancelled) {
    return ECreditApplicationStatusMessage.Cancelled
  }
  if (!isprequal && decision === EDecision.Approved) {
    return ECreditApplicationStatusMessage.Completed
  }
  if ((!decision && !isAnalysing) || (creditApp.consentHardHit && !isAnalysing && decision === EDecision.Pending)) {
    return ECreditApplicationStatusMessage.Error
  }
  if (
    !creditApp.consentHardHit &&
    creditApp.status === ECreditApplicationStatus.Active &&
    (decision === EDecision.Error || decision === EDecision.ManualRevision) &&
    creditApp.originSystemId !== EOriginSystemId.MerchantDashboard
  ) {
    return ECreditApplicationStatusMessage.ManualRevision
  }

  if (requiresMerchantInterventionFlag) {
    return ECreditApplicationStatusMessage.ActionRequired
  }

  if (
    creditApp.consentHardHit &&
    FinancingProgramConfigs[creditApp.financingProgramId as EFinancingProgram].worksheetIsNeededForCredit &&
    creditApp.worksheet === null &&
    isprequal === false &&
    creditApp.originSystemId === EOriginSystemId.Subscription
  ) {
    return decision === EDecision.Refused
      ? ECreditApplicationStatusMessage.Declined
      : ECreditApplicationStatusMessage.AwaitingApproval
  }

  switch (decision) {
    case EDecision.ManualRevision:
      return ECreditApplicationStatusMessage.ManualRevision
    case EDecision.Approved:
      return ECreditApplicationStatusMessage.Completed
    case EDecision.Refused:
      return ECreditApplicationStatusMessage.Declined
    case EDecision.Error:
      return ECreditApplicationStatusMessage.Error
    default:
      return null
  }
}

export const GetIncomeVerificationStatus = (
  applicant: ApplicantDto,
  canStartStep: boolean,
  hasIncomeDocumentsPendingReview: boolean,
  hasIncomeDocumentsPendingUpload: boolean,
  hasIncomeDocumentsApproved: boolean,
  isDone: boolean,
  applicationStatus: ECreditApplicationStatus,
  hasIdentityDocumentPendingReview: boolean,
  hasIdentityDocumentApproved: boolean,
): ECreditApplicationStatusMessage | null => {
  if (applicationStatus === ECreditApplicationStatus.Cancelled) {
    return ECreditApplicationStatusMessage.Cancelled
  }

  if (applicant.incomeVerification?.hasPendingFraudAlert) {
    return ECreditApplicationStatusMessage.ManualRevision
  }

  if (!canStartStep) {
    return null
  }

  const mismatch = applicant.incomeVerification?.incomeMismatch || applicant.incomeVerification?.nameMismatch
  const isWaitingForClient = !!applicant.incomeVerification?.requiredExternalStep && !isDone && !mismatch
  const nameMismatchUnderReview =
    applicant.incomeVerification?.nameMismatch && (hasIdentityDocumentPendingReview || hasIdentityDocumentApproved)
  const hasDocumentsPendingUpload = hasIncomeDocumentsPendingUpload && !isDone && !nameMismatchUnderReview
  const isUnderReview =
    nameMismatchUnderReview ||
    (!hasIncomeDocumentsPendingUpload && hasIncomeDocumentsPendingReview) ||
    (hasIncomeDocumentsApproved && !isDone)
  const hasNotStartedThirdPartyVerification = !applicant.incomeVerification?.requiredExternalStep

  switch (true) {
    case isWaitingForClient:
      return ECreditApplicationStatusMessage.Progress
    case hasDocumentsPendingUpload:
      return ECreditApplicationStatusMessage.DocumentsRequired
    case isDone:
      return ECreditApplicationStatusMessage.Completed
    case isUnderReview:
      return ECreditApplicationStatusMessage.ManualRevision
    case hasNotStartedThirdPartyVerification:
      return ECreditApplicationStatusMessage.Waiting
    default:
      return null
  }
}

const getFundingStatusValue = (creditApp: CreditApplication): ECreditApplicationStatusMessage => {
  if (anyDocsAwaitingUploadOrRefused(creditApp.requiredDocuments, ECreditApplicationStep.PayOut)) {
    return ECreditApplicationStatusMessage.DocumentsRequired
  }
  if (anyDocsAwaitingApproval(creditApp.requiredDocuments)) {
    return ECreditApplicationStatusMessage.ManualRevision
  }
  if (!creditApp.loanCreated) {
    return ECreditApplicationStatusMessage.Processing
  }
  if (creditApp.loanCreated && creditApp.status !== ECreditApplicationStatus.Completed) {
    return ECreditApplicationStatusMessage.Waiting
  }

  return ECreditApplicationStatusMessage.Completed
}

// Funding status + optional banner description key (single selector, full credit app in).
export const GetFundingStatus = (creditApp: CreditApplication) => {
  const status = getFundingStatusValue(creditApp)
  switch (status) {
    case ECreditApplicationStatusMessage.Processing:
      return { status, descriptionKey: 'viewCreditApplication.fundingProcessing' as const }
    case ECreditApplicationStatusMessage.Waiting:
      return { status, descriptionKey: 'viewCreditApplication.fundingAwaitingActivationDate' as const }
    case ECreditApplicationStatusMessage.Completed:
      return { status, descriptionKey: 'viewCreditApplication.fundingCompleted' as const }
    case ECreditApplicationStatusMessage.DocumentsRequired:
      return { status, descriptionKey: 'viewCreditApplication.requiredDocuments' as const }
    case ECreditApplicationStatusMessage.ManualRevision:
      return { status, descriptionKey: 'viewCreditApplication.documentsAwaitingApproval' as const }
    default:
      return { status, descriptionKey: undefined }
  }
}

const getContractStatusValue = (
  creditApp: CreditApplication,
  worksheet: FilteredWorksheet | null,
): ECreditApplicationStatusMessage | null => {
  const isCvtAwaitingApproval = cvtAwaitingApproval(creditApp.requiredDocuments)
  const isAvailableCreditEqualApprovaleAmount = availableCreditEqualApprovedAmount(creditApp)

  const canCreateContract = checkCanCreateContract(creditApp)

  if (canCreateContract) {
    return ECreditApplicationStatusMessage.ActionRequired
  }

  if (creditApp.status === ECreditApplicationStatus.Cancelled) {
    return ECreditApplicationStatusMessage.Cancelled
  }
  if (anyDocsAwaitingUploadOrRefused(creditApp.requiredDocuments, ECreditApplicationStep.CVT)) {
    return ECreditApplicationStatusMessage.DocumentsRequired
  }

  if (
    anyDocsAwaitingApproval(
      creditApp.requiredDocuments.filter(
        (doc: { requiredBeforeStep: ECreditApplicationStep }) => doc.requiredBeforeStep === ECreditApplicationStep.CVT,
      ),
    )
  ) {
    return ECreditApplicationStatusMessage.AwaitingApproval
  }

  if (
    (worksheet == null && !creditApp.hasTasksBeforeCvt) ||
    (worksheet?.status === EWorksheetStatus.Draft &&
      worksheet?.finalDecision.decision === EDecision.Pending &&
      !creditApp.hasTasksBeforeCvt) ||
    (worksheet?.status === EWorksheetStatus.Draft && isAvailableCreditEqualApprovaleAmount)
  ) {
    return creditApp.originSystemId !== EOriginSystemId.MerchantDashboard
      ? ECreditApplicationStatusMessage.ManualRevision
      : ECreditApplicationStatusMessage.ActionRequired
  }
  if (
    worksheet?.finalDecision.decision === EDecision.ManualRevision ||
    isCvtAwaitingApproval ||
    creditApp.hasTasksBeforeCvt
  ) {
    return ECreditApplicationStatusMessage.AwaitingApproval
  }
  if (
    worksheet?.finalDecision.decision === EDecision.Approved &&
    (creditApp.contract?.status === EContractStatus.Ready || creditApp.contract?.status === EContractStatus.Sending)
  ) {
    return ECreditApplicationStatusMessage.AwaitingSignature
  }
  if (worksheet?.status === EWorksheetStatus.Active && creditApp.contract?.status === EContractStatus.Completed) {
    return ECreditApplicationStatusMessage.Completed
  }
  if (creditApp.contract.status === EContractStatus.NotReady && !canPollCreditApp(creditApp)) {
    return ECreditApplicationStatusMessage.Error
  }
  return null
}

// Contract status + optional banner description key (single selector, full credit app in).
export const GetContractStatus = (creditApp: CreditApplication, worksheet: FilteredWorksheet | null) => {
  const status = getContractStatusValue(creditApp, worksheet)
  return {
    status,
    descriptionKey:
      status === ECreditApplicationStatusMessage.Completed
        ? ('viewCreditApplication.stageBanner.completedMessage' as const)
        : undefined,
  }
}

// isBanned comes from the prequalification decision, so a ban is known before any credit report exists.
export const hasBannedApplicant = (creditApp: CreditApplication): boolean =>
  creditApp.applicant.creditAlerts?.isBanned === true || creditApp.coApplicant?.creditAlerts?.isBanned === true

// Prequalification status (used by the step chip and the stage banner).
const getPrequalStatusValue = (creditApp: CreditApplication): ECreditApplicationStatusMessage | null => {
  const isAnalysing = isAnalysisActive(creditApp)
  const needsSIN = mustAskApplicantSIN(creditApp) || mustAskCoapplicantSIN(creditApp)
  const isRefusedWithReportNotFound =
    creditApp.prequalificationDecision?.decision === EDecision.Refused &&
    (!creditApp.applicant.hasCreditReport || (creditApp.coApplicant && creditApp.coApplicant.hasCreditReport === false))

  if (creditApp.prequalificationDecision?.decision === EDecision.Approved && needsSIN) {
    return ECreditApplicationStatusMessage.SinRequired
  }
  if (hasBannedApplicant(creditApp) && creditApp.status !== ECreditApplicationStatus.Cancelled) {
    return ECreditApplicationStatusMessage.Declined
  }
  if (isRefusedWithReportNotFound && creditApp.status !== ECreditApplicationStatus.Cancelled) {
    return ECreditApplicationStatusMessage.CreditReportNotFound
  }
  return GetStatusFromDecision(
    creditApp.prequalificationDecision?.decision,
    true,
    creditApp,
    isAnalysing,
    anyDocsAwaitingApproval(creditApp.requiredDocuments),
  )
}

// Banner description key for the prequal step (undefined when the content carries the message).
const getPrequalDescriptionKey = (creditApp: CreditApplication, status: ECreditApplicationStatusMessage | null) => {
  const isMerchDb = creditApp.originSystemId === EOriginSystemId.MerchantDashboard
  if (status === ECreditApplicationStatusMessage.CreditReportNotFound) {
    if (!creditApp.applicant.hasCreditReport) {
      return isMerchDb
        ? ('viewCreditApplication.applicantCreditReportNotFound' as const)
        : ('viewCreditApplication.applicantCreditReportNotFoundOriginNotMerchDb' as const)
    }
    return isMerchDb
      ? ('viewCreditApplication.coapplicantCreditReportNotFound' as const)
      : ('viewCreditApplication.coapplicantCreditReportNotFoundOriginNotMerchDb' as const)
  }
  // A soft-hit not consented behaves like a decline (same message).
  if (status === ECreditApplicationStatusMessage.Declined || !creditApp.consentSoftHit) {
    return 'viewCreditApplication.clientDeclined' as const
  }
  if (status === ECreditApplicationStatusMessage.DocumentsRequired) {
    return 'viewCreditApplication.requiredDocuments' as const
  }
  if (status === ECreditApplicationStatusMessage.AwaitingApproval) {
    return 'viewCreditApplication.clientManualRevision' as const
  }
  return undefined
}

// Prequalification status + optional banner description key (single selector, full credit app in).
export const GetPrequalStatus = (creditApp: CreditApplication) => {
  const status = getPrequalStatusValue(creditApp)
  return { status, descriptionKey: getPrequalDescriptionKey(creditApp, status) }
}

// Income status for a single applicant (reuses GetIncomeVerificationStatus).
const getIncomeStatusForApplicant = (
  applicant: ApplicantDto,
  requiredDocuments: RequiredDocument[],
  applicationStatus: ECreditApplicationStatus,
  canStartStep: boolean,
): ECreditApplicationStatusMessage | null => {
  const applicantType = applicant.isPrimaryApplicant ? EApplicantType.Applicant : EApplicantType.CoApplicant
  const incomeTypes = getIncomeVerificationDocumentsTypes
  const identityTypes = getIdentityDocumentTypes

  return GetIncomeVerificationStatus(
    applicant,
    canStartStep,
    hasRequiredDocuments(requiredDocuments, incomeTypes, applicantType, EDocumentStatus.AwaitingApproval),
    hasRequiredDocuments(requiredDocuments, incomeTypes, applicantType, EDocumentStatus.AwaitingDocument),
    hasRequiredDocuments(requiredDocuments, incomeTypes, applicantType, EDocumentStatus.Approved),
    applicant.incomeVerification?.allIncomeTasksConfirmed ?? false,
    applicationStatus,
    hasRequiredDocuments(requiredDocuments, identityTypes, applicantType, EDocumentStatus.AwaitingApproval),
    hasRequiredDocuments(requiredDocuments, identityTypes, applicantType, EDocumentStatus.Approved),
  )
}

// Income step status priority: most-actionable first (used to pick between the two applicants).
const INCOME_STATUS_PRIORITY: ECreditApplicationStatusMessage[] = [
  ECreditApplicationStatusMessage.Cancelled,
  ECreditApplicationStatusMessage.DocumentsRequired,
  ECreditApplicationStatusMessage.Waiting,
  ECreditApplicationStatusMessage.Progress,
  ECreditApplicationStatusMessage.ManualRevision,
  ECreditApplicationStatusMessage.Completed,
]

// Income step status: the most actionable of the two applicants' statuses.
export const getIncomeStatus = (creditApp: CreditApplication): ECreditApplicationStatusMessage | null => {
  const canStartStep = creditApp.finalCreditDecision.decision === EDecision.Approved
  const applicants = creditApp.coApplicant ? [creditApp.applicant, creditApp.coApplicant] : [creditApp.applicant]
  const statuses = applicants
    .map((a) => getIncomeStatusForApplicant(a, creditApp.requiredDocuments, creditApp.status, canStartStep))
    .filter((s): s is ECreditApplicationStatusMessage => s !== null)

  if (statuses.length === 0) return null

  const rank = (s: ECreditApplicationStatusMessage) => {
    const index = INCOME_STATUS_PRIORITY.indexOf(s)
    return index === -1 ? Number.MAX_SAFE_INTEGER : index
  }
  return statuses.reduce((best, s) => (rank(s) < rank(best) ? s : best))
}

// Derives the current step from the same gates the tiles apply (prequal → credit → income → contract → funding).
export const getCurrentStep = (creditApp: CreditApplication): ECurrentCustomerStep => {
  const prequalDecision = creditApp.prequalificationDecision?.decision
  const finalDecision = creditApp.finalCreditDecision.decision

  if (creditApp.status === ECreditApplicationStatus.Draft) {
    return ECurrentCustomerStep.Draft
  }
  if (creditApp.status === ECreditApplicationStatus.Active && prequalDecision !== EDecision.Approved) {
    return ECurrentCustomerStep.Prequal
  }
  if (prequalDecision === EDecision.Approved && finalDecision !== EDecision.Approved) {
    return ECurrentCustomerStep.Credit
  }
  if (finalDecision === EDecision.Approved && !creditApp.allIncomesConfirmed) {
    return ECurrentCustomerStep.IncomeVerification
  }
  if (!isFundingStarted(creditApp)) {
    return ECurrentCustomerStep.Contract
  }
  return ECurrentCustomerStep.Funding
}

// Returns a step's status message — used by the header chip (active step) and the stage banner (selected step).
export const getStepStatus = (
  step: ECurrentCustomerStep,
  creditApp: CreditApplication,
): ECreditApplicationStatusMessage | null => {
  const isAnalysing = isAnalysisActive(creditApp)

  switch (step) {
    case ECurrentCustomerStep.Prequal:
      return GetPrequalStatus(creditApp).status
    case ECurrentCustomerStep.Credit:
      return GetStatusFromDecision(
        creditApp.finalCreditDecision.decision,
        false,
        creditApp,
        isAnalysing,
        anyDocsAwaitingApproval(creditApp.requiredDocuments),
      )
    case ECurrentCustomerStep.IncomeVerification:
      return getIncomeStatus(creditApp)
    case ECurrentCustomerStep.Contract:
      return GetContractStatus(creditApp, creditApp.worksheet).status
    case ECurrentCustomerStep.Funding:
      return GetFundingStatus(creditApp).status
    default:
      return null
  }
}
